import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/DashboardLayout';
import ExpenseEditModal from '../components/ExpenseEditModal';
import toast from 'react-hot-toast';
import { getCategoryTranslator } from '../utils/categoryTranslation';
import { nativeFetch } from '../utils/nativeApi';

function Dashboard() {
  const { t } = useTranslation(['dashboard', 'expenses', 'common', 'categories']);
  const translateCategory = getCategoryTranslator(t);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [textInput, setTextInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [recentExpenses, setRecentExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(true);
  const [editingExpense, setEditingExpense] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch recent expenses
  useEffect(() => {
    fetchRecentExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveExpense = (updatedExpense) => {
    setRecentExpenses(recentExpenses.map(e => e.id === updatedExpense.id ? updatedExpense : e));
  };

  const fetchRecentExpenses = async () => {
    try {
      const response = await nativeFetch('/api/expenses?limit=2');
      if (response.ok) {
        const data = await response.json();
        setRecentExpenses(data.expenses || []);
      }
    } catch (error) {
      console.error('Error fetching recent expenses:', error);
    } finally {
      setLoadingExpenses(false);
    }
  };

  const startRecording = async () => {
    try {
      // First, get microphone access
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('[Recording] Microphone access granted');
      } catch (micError) {
        console.error('[Recording] Microphone access denied:', micError.name, micError.message);
        toast.error('Microphone access denied. Please allow microphone permissions.');
        return;
      }
      
      // Determine supported MIME type (iOS Safari doesn't support webm)
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
        ''  // Empty string = browser default
      ];
      
      let selectedMimeType = '';
      for (const mimeType of mimeTypes) {
        if (mimeType === '' || MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          console.log('[Recording] Using MIME type:', mimeType || 'browser default');
          break;
        }
      }
      
      // Create MediaRecorder with supported options
      let mediaRecorder;
      try {
        const options = selectedMimeType ? { mimeType: selectedMimeType } : undefined;
        mediaRecorder = new MediaRecorder(stream, options);
        console.log('[Recording] MediaRecorder created, actual mimeType:', mediaRecorder.mimeType);
      } catch (recorderError) {
        console.error('[Recording] Failed to create MediaRecorder:', recorderError.name, recorderError.message);
        stream.getTracks().forEach(track => track.stop());
        toast.error('Recording not supported on this device');
        return;
      }
      
      const actualMimeType = mediaRecorder.mimeType;
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: actualMimeType });
        console.log('[Recording] Recording stopped, blob size:', audioBlob.size, 'type:', actualMimeType);
        await processVoiceInput(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer (max 45 seconds)
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= 45) {
            stopRecording();
            return 45;
          }
          return prev + 1;
        });
      }, 1000);

    } catch (error) {
      console.error('[Recording] Unexpected error:', error.name, error.message);
      toast.error('Failed to start recording: ' + error.message);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsProcessing(true); // Show processing immediately
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Remove the onstop handler to prevent processing
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
      
      // Stop all tracks to release microphone
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
      
      setIsRecording(false);
      setRecordingTime(0);
      audioChunksRef.current = [];
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      toast('Recording cancelled', { icon: '🚫' });
    }
  };

  const processVoiceInput = async (audioBlob) => {
    setIsProcessing(true);
    
    try {
      // Convert blob to base64
      const base64Audio = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });
      
      console.log('[Voice] Sending audio, base64 length:', base64Audio.length);
      
      const response = await nativeFetch('/api/expenses', {
        method: 'POST',
        body: JSON.stringify({
          input_type: 'voice',
          content: base64Audio,
        }),
      });

      console.log('[Voice] Response status:', response.status, 'ok:', response.ok);
      const data = await response.json();
      console.log('[Voice] Response data:', JSON.stringify(data));

      if (response.ok) {
        toast.success('Expense created successfully!');
        fetchRecentExpenses();
        console.log('Created expense:', data.expense);
      } else {
        toast.error(data.error || 'Failed to create expense');
      }
    } catch (error) {
      console.error('Error processing voice input:', error);
      toast.error('Failed to process voice input');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTextSubmit = async (e) => {
    e.preventDefault();
    
    if (!textInput.trim()) {
      toast.error('Please enter an expense description');
      return;
    }

    if (textInput.length > 1024) {
      toast.error('Text is too long (max 1024 characters)');
      return;
    }

    setIsProcessing(true);

    try {
      const response = await nativeFetch('/api/expenses', {
        method: 'POST',
        body: JSON.stringify({
          input_type: 'text',
          content: textInput,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Expense created successfully!');
        setTextInput('');
        fetchRecentExpenses();
        console.log('Created expense:', data.expense);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to create expense');
      }
    } catch (error) {
      console.error('Error submitting text input:', error);
      toast.error('Failed to create expense');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCameraClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('expenses:receipt.invalidImage'));
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('expenses:receipt.imageTooLarge'));
      return;
    }

    setIsProcessing(true);

    try {
      // Convert image to base64
      const base64Image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      console.log('[Receipt] Sending image, base64 length:', base64Image.length);

      const response = await nativeFetch('/api/expenses', {
        method: 'POST',
        body: JSON.stringify({
          input_type: 'image',
          content: base64Image,
        }),
      });

      console.log('[Receipt] Response status:', response.status, 'ok:', response.ok);
      const data = await response.json();
      console.log('[Receipt] Response data:', JSON.stringify(data));

      if (response.ok) {
        toast.success(t('expenses:receipt.success'));
        fetchRecentExpenses();
        console.log('Created expense from receipt:', data.expense);
      } else {
        toast.error(data.error || t('expenses:receipt.failed'));
      }
    } catch (error) {
      console.error('Error processing receipt image:', error);
      toast.error(t('expenses:receipt.failed'));
    } finally {
      setIsProcessing(false);
      // Reset file input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col items-center px-4 py-3 pb-24">
        {/* Hidden file input for receipt capture */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleImageSelect}
          className="hidden"
        />

        {/* Main Record Button with Camera Button */}
        <div className="flex w-full justify-center items-center gap-4 py-4">
          {!isRecording && !isProcessing && (
            <>
              {/* Main Record Button */}
              <button
                onClick={startRecording}
                className="flex h-36 w-36 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-black bg-accent text-black shadow-hard transition-transform active:translate-x-1 active:translate-y-1 active:shadow-none"
              >
                <span className="text-6xl">🎤</span>
              </button>
              
              {/* Camera Button */}
              <button
                onClick={handleCameraClick}
                className="flex h-14 w-14 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-black bg-white text-black shadow-hard-sm transition-transform active:translate-x-0.5 active:translate-y-0.5 active:shadow-none hover:bg-gray-50"
                title={t('expenses:receipt.scanReceipt')}
              >
                <span className="text-2xl">📷</span>
              </button>
            </>
          )}

          {isRecording && (
            <div className="relative">
              {/* Cancel button in corner */}
              <button
                onClick={cancelRecording}
                className="absolute -top-2 -right-2 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-2 border-black bg-white text-black shadow-hard-sm hover:bg-gray-100 transition-colors"
                title="Cancel recording"
              >
                <span className="text-xl">✕</span>
              </button>
              {/* Stop/Submit button */}
              <button
                onClick={stopRecording}
                className="flex h-36 w-36 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-black bg-red-500 text-white shadow-hard animate-pulse"
              >
                <div className="text-center">
                  <div className="text-4xl mb-2">⏹️</div>
                  <div className="text-lg font-bold">{recordingTime}s</div>
                </div>
              </button>
            </div>
          )}

          {isProcessing && (
            <div className="flex h-36 w-36 items-center justify-center overflow-hidden rounded-full border-2 border-black bg-yellow-400 text-black shadow-hard">
              <div className="text-center">
                <div className="text-4xl mb-2 animate-spin">⏳</div>
                <div className="text-sm font-bold">{t('expenses:recording.processing')}</div>
              </div>
            </div>
          )}
        </div>

        {/* Text Input */}
        <div className="w-full max-w-sm pt-4">
          <form onSubmit={handleTextSubmit}>
            <input
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              className="flex h-14 w-full resize-none overflow-hidden rounded-xl border-2 border-black bg-white p-4 text-base font-normal text-black shadow-hard-sm placeholder:text-black/50 focus:border-black focus:ring-0 focus:outline-none"
              placeholder={t('expenses:recording.placeholder')}
              maxLength={1024}
              disabled={isProcessing}
            />
          </form>
        </div>

        {/* Recent Expenses Section */}
        <div className="w-full max-w-sm pt-5">
          <h2 className="text-lg font-bold text-black mb-3">{t('recentExpenses')}</h2>
          {loadingExpenses ? (
            <div className="text-center text-black/60">{t('common:messages.loading')}</div>
          ) : recentExpenses.length === 0 ? (
            <div className="text-center text-black/60">{t('noExpenses')}</div>
          ) : (
            <div className="flex flex-col gap-3">
              {recentExpenses.map((expense) => (
                <div
                  key={expense.id}
                  className="flex items-center justify-between rounded-xl border-2 border-black bg-white p-3 shadow-hard-sm cursor-pointer hover:shadow-hard-md transition-shadow"
                  onClick={() => setEditingExpense(expense)}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg border-2 border-black bg-primary/20">
                      <span className="text-xl">
                        {expense.category?.icon || '💰'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-black">{expense.title || 'Expense'}</p>
                      <p className="text-xs text-black/60">
                        {expense.category?.name ? translateCategory(expense.category.name) : (expense.payment_method?.name || t('common:messages.noData'))}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm font-bold text-black">
                    {expense.amount} {expense.currency}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <ExpenseEditModal
        expense={editingExpense}
        isOpen={!!editingExpense}
        onClose={() => setEditingExpense(null)}
        onSave={handleSaveExpense}
        onDelete={(deletedId) => setRecentExpenses(recentExpenses.filter(e => e.id !== deletedId))}
      />
    </DashboardLayout>
  );
}

export default Dashboard;
